import { Injectable } from '@angular/core';
import { UserInfo } from './user-info.model';

@Injectable()
export class UserCacheService {

  private cachedLists: UserListCache[] = [];

  constructor() {
  }

  aggregate(users: UserInfo[], listType: UserListType, predicate?: (u: UserInfo) => boolean): UserInfo[] {
    const cacheList: UserListCache = this.getListCache(listType);

    this.aggregateWithNewUsers(users, cacheList, predicate);
    this.aggregateWithUpdatedUsers(users, cacheList, predicate);
    this.aggregateWithRemovedUsers(users, cacheList);

    return users;
  }

  registerNewUser(user: UserInfo, listType: UserListType) {
    this.getListCache(listType).newUsers.push(user);
  }

  registerUpdatedUser(user: UserInfo, listType: UserListType) {
    const cachedList = this.getListCache(listType);
    const index      = cachedList.updatedUsers.findIndex(u => u.id === user.id);

    if (index === -1) {
      cachedList.updatedUsers.push(user);
    } else {
      cachedList.updatedUsers[index] = user;
    }
  }

  registerRemovedUser(userId: string, listType: UserListType) {
    const user = new UserInfo();
    user.id    = userId;

    const cacheList = this.getListCache(listType);

    this.removeUserFromList(user, cacheList.newUsers);

    cacheList.removedUsers.push(user);
  }

  private aggregateWithNewUsers(users: UserInfo[], cachedList: UserListCache, predicate?: (u: UserInfo) => boolean) {
    for (const newUser of cachedList.newUsers) {
      if (predicate && !predicate(newUser)) {
        continue;
      }

      const index = users.findIndex(u => u.id === newUser.id);

      if (index === -1) {
        console.log('Added cached user to list');
        users.push(newUser);
      } else {
        console.log('Removed user from new users cache');
        this.removeUserFromList(newUser, cachedList.newUsers);
      }
    }
  }

  private aggregateWithUpdatedUsers(users: UserInfo[], cachedList: UserListCache, predicate?: (u: UserInfo) => boolean) {
    for (const updatedUser of cachedList.updatedUsers) {
      if (predicate && !predicate(updatedUser)) {
        continue;
      }

      const index = users.findIndex(u => u.id === updatedUser.id);

      if (index !== -1 && updatedUser.updateDateTimestamp > users[index].updateDateTimestamp) {
        console.log('Updated cached user to list');
        users[index] = updatedUser;
      } else {
        console.log('Removed user from updated users cache');
        this.removeUserFromList(updatedUser, cachedList.updatedUsers);
      }
    }
  }

  private aggregateWithRemovedUsers(users: UserInfo[], cachedList: UserListCache) {
    for (const removedUser of cachedList.removedUsers) {
      const index = users.findIndex(u => u.id === removedUser.id);

      if (index === -1) {
        console.log('Removed user from removed users cache');
        this.removeUserFromList(removedUser, cachedList.removedUsers);
      } else {
        console.log('Removed cached user from list');
        this.removeUserFromList(removedUser, users);
      }
    }
  }


  private getListCache(listType: UserListType): UserListCache {
    let cacheList = this.cachedLists.find(l => l.type === listType);

    if (!cacheList) {
      cacheList = {
        type: listType,
        newUsers: [],
        updatedUsers: [],
        removedUsers: []
      };

      this.cachedLists.push(cacheList);
    }

    return cacheList;
  }

  private removeUserFromList(user: UserInfo, userList: UserInfo[]) {
    const index = userList.findIndex(u => u.id === user.id);
    if (index !== -1) {
      userList.splice(index, 1);
    }
  }

}

interface UserListCache {

  type: any;
  newUsers: UserInfo[];
  updatedUsers: UserInfo[];
  removedUsers: UserInfo[];
}

export enum UserListType {
  USERS            = 'USERS',
  ADMINS           = 'ADMINS',
  DRIVERS          = 'DRIVERS',
  CARRIER_MANAGERS = 'CARRIER_MANAGERS',
  CHEP_MANAGERS    = 'CHEP_MANAGERS',
  PLANT_MANAGERS   = 'PLANT_MANAGERS',
  CHEP_SERVICE_REP = 'CHEP_SERVICE_REP',
}
